import { createContext, useContext, useMemo, ReactNode } from "react";

import {
  useSpendSavings,
  SpendSavingsMonth,
  calculateLevel,
  calculateLevelProgress,
  getNextLevelThreshold,
} from "./api";
import { getAchievements, Achievement } from "./constants";

interface MoneySavedContextValue {
  isLoading: boolean;
  error: Error | null;
  totalSavings: number;
  level: number;
  levelProgress: number;
  nextLevelThreshold: number;
  months: SpendSavingsMonth[];
  achievements: Achievement[];
  capiConnectedDate: string | null;
  cacReductionPct: number | null;
  totalSpend: number;
  totalConversions: number;
  totalExtraConversions: number;
}

const MoneySavedContext = createContext<MoneySavedContextValue | null>(null);

export function MoneySavedProvider({ children }: { children: ReactNode }) {
  const { data, isLoading, error } = useSpendSavings();

  const value = useMemo<MoneySavedContextValue>(() => {
    const totalSavings = data?.total_savings ?? 0;
    const level = calculateLevel(totalSavings);
    const levelProgress = calculateLevelProgress(totalSavings, level);
    const nextLevelThreshold = getNextLevelThreshold(level);
    const months = data?.months ?? [];
    const achievements = getAchievements(totalSavings);

    const totalSpend = months.reduce((sum, m) => sum + (m.spend_usd ?? 0), 0);
    const totalConversions = months.reduce(
      (sum, m) => sum + (m.low_funnel_conversions ?? 0),
      0,
    );
    const totalExtraConversions = months.reduce(
      (sum, m) => sum + Math.max(0, m.extra_conversions ?? 0),
      0,
    );

    return {
      isLoading,
      error: error as Error | null,
      totalSavings,
      level,
      levelProgress,
      nextLevelThreshold,
      months,
      achievements,
      capiConnectedDate: data?.capi_connected_date ?? null,
      cacReductionPct: data?.cac_reduction_pct ?? null,
      totalSpend,
      totalConversions,
      totalExtraConversions,
    };
  }, [data, isLoading, error]);

  return (
    <MoneySavedContext.Provider value={value}>
      {children}
    </MoneySavedContext.Provider>
  );
}

export function useMoneySaved() {
  const context = useContext(MoneySavedContext);
  if (!context) {
    throw new Error("useMoneySaved must be used within a MoneySavedProvider");
  }
  return context;
}
